import * as StringHelpers from '@helpers/StringHelpers';

/**
 * Check if variable is object
 * @param {*} obj
 * @returns {bool}
 */
export function isObject(obj) {
  return typeof obj === 'object' && obj !== null;
}

/**
 * Check if an object is empty
 * @param {*} obj
 * @param {boolean} strict - if strict, will return false for non-objects
 * @returns {boolean}
 */
export function isObjectEmpty(obj, strict = false) {
  if (strict) {
    if (typeof obj !== 'object') {
      return false;
    }
    return Object.values(obj).filter((i) => !!i).length === 0;
  }
  return obj && Object.values(obj).filter((i) => !!i).length === 0;
}

/**
 * Map over the values of an object
 * @param {Object} object
 * @function mapFn
 * @returns {Object}
 */
export function objectMap(object, mapFn) {
  if (!object) return {};
  return Object.keys(object).reduce((result, key) => {
    result[key] = mapFn(object[key], key);
    return result;
  }, {});
}

/**
 * Reduce an object by mapping over the keys
 * @param {Object} object
 * @function mapFn
 * @param {string | number} initial
 * @returns {string | number}
 */
export function objectReduce(object, mapFn, initial = '') {
  if (!object) return {};
  return Object.keys(object).reduce((acc, key, index) => {
    return mapFn(acc, key, object[key], index);
  }, initial);
}

/**
 * Clean object of empty keys
 * @param {Object} object
 * @returns {Object}
 */
export function objectClean(object) {
  if (!object) return {};
  return Object.fromEntries(Object.entries(object).filter(([_, value]) => value != null && value !== ''));
}

/**
 * Create object map from an array
 * @param {Object} array
 * @param {string} key
 * @returns {Object}
 */
export function createObjectMap(array, key, grouped) {
  if (!array) return {};

  if (grouped) {
    return array.reduce((acc, item) => {
      const mapKey = typeof key === 'function' ? key(item) : item[key];
      if (!acc[mapKey]) {
        acc[mapKey] = [];
      }
      acc[mapKey].push(item);
      return acc;
    }, {});
  }

  return array.reduce((acc, item) => {
    const mapKey = typeof key === 'function' ? key(item) : item[key];
    acc[mapKey] = item;
    return acc;
  }, {});
}

/**
 * Check if an object keys match a given search query
 * @param {Object} object
 * @param {string} searchQuery
 * @param {{ keys: Array, caseSensitive: boolean, replaceDiacritics: boolean, separated: boolean }} options
 * @returns {boolean}
 */
export function isSearchMatch(object, searchQuery = '', options = {}) {
  try {
    const {
      keys,
      caseSensitive = false,
      replaceDiacritics = true,
      separated = false,
    } = options;

    const objectKeys = (keys && Array.isArray(keys))
      ? keys
      : Object.keys(object);

    const transformedSearchQuery = transformString({
      string: searchQuery,
      caseSensitive,
      replaceDiacritics,
      trimmed: true,
      separated
    });

    return objectKeys.some((key) => {
      const value = transformString({
        string: object[key],
        caseSensitive,
        replaceDiacritics,
      });

      if (Array.isArray(transformedSearchQuery)) {
        return transformedSearchQuery.some((q) => value?.includes(q));
      }

      return value?.includes(transformedSearchQuery);
    });
  } catch (err) {
    console.error('Failed to search object', err);
  }
  return false;
}

// Helpers

/**
 * Transform a given string
 * @param {{ string: string, caseSensitive: boolean, replaceDiacritics: boolean, trimmed: boolean, separated: boolean }}
 * @returns {string}
 */
const transformString = ({
  string, caseSensitive, replaceDiacritics, trimmed, separated
}) => {
  let parsedString = string;

  if (!caseSensitive) {
    parsedString = parsedString?.toLowerCase();
  }

  if (replaceDiacritics) {
    parsedString = StringHelpers.replaceDiacritics(parsedString);
  }

  if (trimmed) {
    parsedString = parsedString?.trim();
  }

  if (separated) {
    parsedString = parsedString?.split(/[,.\s]/).filter((s) => !!s);
  }

  return parsedString;
};
